import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Breadcrumb,
  Badge,
  Card,
  Typography,
  Divider,
  Space,
  message,
  Button,
  Spin,
  Tag,
  Switch,
  Select,
} from "antd";
import { useAuth0 } from "../../react-auth0-spa";
import config from "../../api_config.json";
import { useAppContext } from "../../context";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  rectIntersection,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import { LazyLoadImage } from "react-lazy-load-image-component";
import axios from "axios";
import InfiniteScroll from "react-infinite-scroll-component";
import EdgeResizer from "../common/utils/EdgeResizer";

const { Title } = Typography;

const GiottoAssetOrganiser = (props) => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [isOrderChanged, setOrderChanged] = useState(false);
  const [showTags, setShowTags] = useState(false);
  const [orientation, setOrientation] = useState("all"); // 'all' | 'h' | 'v' | 's'
  const { isProd } = useAppContext();
  
  // Use ref to track items length without causing fetchPage to recreate
  const itemsRef = useRef([]);

  const { getTokenSilently } = useAuth0();

  const pageSize = props.pageSize || 100;

  const canShowTagsCollection = useMemo(() => (
    props.collection === 'editor_images_default' || props.collection === 'editor_backgrounds_default'
  ), [props.collection]);

  const pluralQueryField = useMemo(() => `${props.collection}s`, [props.collection]);

  const orderEnum = useMemo(() => {
    // Build the correct enum type name based on collection
    // Example: editor_images_default -> Editor_images_defaultOrder
    const base = props.collection
      .split("")
      .map((c, i) => (i === 0 ? c.toUpperCase() : c))
      .join("");
    return `${base}Order`;
  }, [props.collection]);

  const whereInputName = useMemo(() => {
    const base = props.collection
      .split("")
      .map((c, i) => (i === 0 ? c.toUpperCase() : c))
      .join("");
    return `${base}WhereInput`;
  }, [props.collection]);

  const buildAssetsQuery = useCallback(() => {
    // Select fields based on collection type
    const isImages = props.collection === "editor_images_default";
    const isSymbols = props.collection === "editor_symbols_default";
    const isBackgrounds = props.collection === "editor_backgrounds_default";

    let fileFields = "";
    if (isImages) {
      fileFields = `
        file { url }
        custom_preview { url }
      `;
    } else if (isSymbols) {
      fileFields = `
        img { url }
      `;
    } else if (isBackgrounds) {
      fileFields = `
        file { url }
      `;
    }

    const tagsField = isSymbols ? "" : `
              tags(first: 1000, order: [name_ASC]) {
                edges { node { name } }
              }`;

    return `
      query AssetsQuery($first: Int!, $skip: Int, $order: [${orderEnum}!], $where: ${whereInputName}) {
        ${pluralQueryField}(
          first: $first,
          skip: $skip,
          order: $order,
          where: $where
        ) {
          count
          edges {
            node {
              objectId
              order
              ${fileFields}
              ${tagsField}
            }
          }
        }
      }
    `.replace(/\r?\n|\r/g, " ").replace(/\t/g, " ");
  }, [pluralQueryField, orderEnum, whereInputName, props.collection]);

  const whereFilter = useMemo(() => {
    if (
      props.collection === "editor_backgrounds_default" &&
      (orientation === "h" || orientation === "v" || orientation === "s")
    ) {
      return { orientation: { equalTo: orientation } };
    }
    return null;
  }, [props.collection, orientation]);

  const fetchPage = useCallback(async (append = false) => {
    const env = isProd ? "prod" : "dev";
    const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
    try {
      if (!append) setLoading(true);
      else setLoadingMore(true);

      const token = await getTokenSilently();
      const variables = {
        first: pageSize,
        skip: append ? itemsRef.current.length : 0,
        order: ["order_ASC"],
        where: whereFilter,
      };

      const response = await axios({
        method: "post",
        url: graphqlUrl,
        data: { query: buildAssetsQuery(), variables },
        headers: { Authorization: `Bearer ${token}` },
      });

      const dataKey = pluralQueryField; // response.data has top-level field per docs
      const connection = response.data?.[dataKey];
      if (connection) {
        const newItems = (connection.edges || []).map((e) => e.node);
        setTotalCount(connection.count || 0);
        if (append) {
          setItems((prev) => {
            const updated = [...prev, ...newItems];
            itemsRef.current = updated;
            return updated;
          });
        } else {
          setItems(newItems);
          itemsRef.current = newItems;
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (!append) setLoading(false);
      else setLoadingMore(false);
    }
  }, [isProd, getTokenSilently, buildAssetsQuery, pluralQueryField, pageSize, whereFilter]);

  useEffect(() => {
    // reset state on collection/env change and fetch first page once
    setItems([]);
    itemsRef.current = [];
    setTotalCount(0);
    setOrderChanged(false);
    setOrientation('all');
    fetchPage(false);
    // Intentionally limit deps to collection/env to avoid duplicate initial fetches
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.collection, isProd]);

  // Refetch when orientation changes so query uses the latest selected value
  useEffect(() => {
    setItems([]);
    setTotalCount(0);
    itemsRef.current = [];
    fetchPage(false);
  }, [orientation, fetchPage]);

  const updateItemOrder = async (objectClass, objectId, order) => {
    const env = isProd ? "prod" : "dev";
    const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
    const token = await getTokenSilently();

    // Build mutation name based on class
    // Example: editor_images_default -> updateEditor_images_default
    const mutationName = `update${objectClass
      .split("")
      .map((c, i) => (i === 0 ? c.toUpperCase() : c))
      .join("")}`;

    const mutation = `
      mutation UpdateOrder($id: ID!, $order: Float) {
        ${mutationName}(input: { id: $id, fields: { order: $order } }) {
          clientMutationId
        }
      }
    `.replace(/\r?\n|\r/g, " ").replace(/\t/g, " ");

    await axios({
      method: "post",
      url: graphqlUrl,
      data: { query: mutation, variables: { id: objectId, order } },
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const grid = 4;

  const containerStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: grid,
    width: `${props.widthPercent || 50}%`,
    minWidth: 480,
    boxSizing: 'border-box',
    padding: grid,
    background: "lightgrey"
  };

  const handleDragEnd = (event) => {
    const {active, over} = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex(item => item.objectId === active.id);
      const newIndex = items.findIndex(item => item.objectId === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);
      itemsRef.current = newItems;
      setOrderChanged(true);
    }
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // SortableItem component
  const SortableItem = ({ item, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: item.objectId });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      userSelect: "none",
      padding: grid,
      background: isDragging ? "lightgreen" : "",
      opacity: isDragging ? 0.5 : 1,
    };

    return (
      <div
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...listeners}
      >
        <Card size="small" bodyStyle={{ padding: 8 }} style={{ textAlign: "center" }}>
          <LazyLoadImage
            style={{
              marginBottom: "4px",
              width: "auto",
              maxWidth: "100%",
              height: "140px",
              objectFit: "contain",
              objectPosition: "center center",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
              background: "#fff",
              borderRadius: 4,
              boxShadow: "rgba(0, 0, 0, 0.06) 0px 0px 0px 1px, rgba(0, 0, 0, 0.08) 0px 2px 6px",
            }}
            src={getOptimizedImageUrl(
              item[props.fileColName]?.url || item.custom_preview?.url
            )}
            alt={item.objectId}
          />
          {showTags && canShowTagsCollection && (
            <div style={{ marginTop: "4px", textAlign: 'left', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {(item?.tags?.edges || [])
                .map((e) => e.node?.name)
                .filter(Boolean)
                .map((name) => (
                  <Tag key={name} color="blue" style={{ marginBottom: 2 }}>
                    {name}
                  </Tag>
                ))}
            </div>
          )}
        </Card>
      </div>
    );
  };

  const hasMore = totalCount ? items.length < totalCount : false;
  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    fetchPage(true);
  }, [loading, loadingMore, hasMore, fetchPage]);

  const currentPage = useMemo(() => Math.max(1, Math.ceil(items.length / pageSize)), [items.length, pageSize]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil((totalCount || 0) / pageSize)), [totalCount, pageSize]);

  const saveNewOrder = async () => {
    message.loading("Aggiornamento in corso. Ci vorranno alcuni minuti. Non chiudere la pagina", 0);
    setOrderChanged(false);

    try {
      // Update order for currently loaded items only
      for (let index = 0; index < items.length; index++) {
        await updateItemOrder(props.collection, items[index].objectId, index);
        await sleep(50);
      }
    } catch (error) {
      message.error("Si é verificato un problema: " + error.message);
      console.error(error);
      setOrderChanged(true);
    }

    message.destroy();
    message.success("Ordine aggiornato con successo");
  };

  const getOptimizedImageUrl = useCallback((url) => {
    if (!url) return "";
    try {
      const env = isProd ? "prod" : "dev";
      const edgeResizer = new EdgeResizer(config.files[env], url);
      // Generate 300px wide thumbnails via EdgeResizer
      return edgeResizer.scaleToWidth(300).toString();
    } catch (e) {
      console.error("EdgeResizer error", e);
      return url;
    }
  }, [isProd]);

  const downloadAll = useCallback(async () => {
    if (downloadingAll || loading || loadingMore) return;
    if (!totalCount || itemsRef.current.length >= totalCount) return;
    setDownloadingAll(true);
    const hide = message.loading("Scaricamento di tutti gli elementi in corso...", 0);
    try {
      const env = isProd ? "prod" : "dev";
      const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;
      const token = await getTokenSilently();

      let offset = itemsRef.current.length;
      while (offset < totalCount) {
        const variables = {
          first: pageSize,
          skip: offset,
          order: ["order_ASC"],
          where: whereFilter,
        };
        const response = await axios({
          method: "post",
          url: graphqlUrl,
          data: { query: buildAssetsQuery(), variables },
          headers: { Authorization: `Bearer ${token}` },
        });
        const dataKey = pluralQueryField;
        const connection = response.data?.[dataKey];
        const newItems = (connection?.edges || []).map(e => e.node);
        if (!newItems.length) break;
        // Append immediately so pagination info updates progressively
        setItems(prev => {
          const updated = [...prev, ...newItems];
          itemsRef.current = updated;
          return updated;
        });
        offset += newItems.length;
      }
      hide();
      message.success("Tutti gli elementi sono stati scaricati");
    } catch (e) {
      hide();
      message.error("Errore durante lo scaricamento di tutti gli elementi");
      console.error(e);
    } finally {
      setDownloadingAll(false);
    }
  }, [downloadingAll, loading, loadingMore, totalCount, isProd, getTokenSilently, pageSize, whereFilter, buildAssetsQuery, pluralQueryField]);

  return (
    <div style={{ margin: "16px 16px" }}>
      <div>{loading && <div style={{ textAlign: 'center', padding: '20px' }}><Spin /></div>}</div>
      <Breadcrumb style={{ marginBottom: "20px" }}>
        <Breadcrumb.Item>Giotto</Breadcrumb.Item>
        <Breadcrumb.Item>{props.collection}</Breadcrumb.Item>
      </Breadcrumb>
      <Divider />
      <Title level={3} style={{ marginBottom: "40px" }}>
        <Space>
          Assets Organiser - {props.collection}
          <Badge overflowCount={5000} count={totalCount || items.length}></Badge>
        </Space>
      </Title>
      <div style={{ marginBottom: 16 }}>
        <Space size="large">
          {canShowTagsCollection && (
            <Space>
              <span>Mostra Tag</span>
              <Switch checked={showTags} onChange={setShowTags} />
            </Space>
          )}
          {props.collection === 'editor_backgrounds_default' && (
            <Space>
              <span>Orientamento</span>
              <Select
                value={orientation}
                style={{ width: 160 }}
                onChange={(val) => {
                  setOrientation(val);
                }}
                options={[
                  { label: 'Tutti', value: 'all' },
                  { label: 'Orizzontale (h)', value: 'h' },
                  { label: 'Verticale (v)', value: 'v' },
                  { label: 'Quadrato (s)', value: 's' },
                ]}
              />
            </Space>
          )}
        </Space>
      </div>
      <Divider />
      <Button
        disabled={!isOrderChanged}
        style={{ marginBottom: "20px" }}
        type="primary"
        size="large"
        onClick={saveNewOrder}
      >
        Salva
      </Button>
      <Button
        disabled={downloadingAll || loading || loadingMore || (totalCount && items.length >= totalCount)}
        style={{ marginLeft: 8, marginBottom: "20px" }}
        size="large"
        onClick={downloadAll}
      >
        Scarica tutti
      </Button>
      <div style={{ marginBottom: 8, color: '#888' }}>
        {totalCount ? (
          <span>
            Caricati {items.length} di {totalCount} (Pagina {currentPage} di {totalPages})
          </span>
        ) : (
          <span>Caricamento in corso...</span>
        )}
      </div>
      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragEnd={handleDragEnd}
      >
        <InfiniteScroll
          dataLength={items.length}
          next={loadMore}
          hasMore={hasMore}
          loader={<Spin size="large" style={{ display: 'block', textAlign: 'center', margin: '20px 0' }} />}
          endMessage={
            items.length > 0 && (
              <div style={{ textAlign: 'center', margin: '20px 0', color: '#999' }}>
                Non ci sono altri elementi da caricare
              </div>
            )
          }
        >
          <SortableContext items={items.map(item => item.objectId)} strategy={rectSortingStrategy}>
            <div style={containerStyle}>
              {items.map((item, index) => (
                <SortableItem key={item.objectId} item={item} index={index} />
              ))}
            </div>
          </SortableContext>
        </InfiniteScroll>
      </DndContext>
    </div>
  );
};

export default GiottoAssetOrganiser;
